/**
 * Equipment API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  Equipment,
  EquipmentListParams,
  EquipmentListResponse,
  EquipmentStats,
  CreateEquipmentRequest,
  UpdateEquipmentRequest,
  AssignEquipmentRequest,
  ReturnEquipmentRequest,
  MaintenanceLogRequest,
} from '../types/equipment';

/**
 * Equipment API Endpoints
 * Base URL: /projects/:projectId/equipment
 */
export const equipmentApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ==========================================================================
    // STATS
    // ==========================================================================

    getEquipmentStats: builder.query<
      ApiResponse<EquipmentStats>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/equipment/stats`,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'EquipmentStats', id: projectId },
      ],
    }),

    // ==========================================================================
    // LIST
    // ==========================================================================

    getEquipment: builder.query<
      ApiResponse<EquipmentListResponse>,
      { projectId: string; params?: EquipmentListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/equipment`,
        params,
      }),
      providesTags: ['Equipment'],
    }),

    // ==========================================================================
    // GET SINGLE
    // ==========================================================================

    getEquipmentById: builder.query<
      ApiResponse<Equipment>,
      { projectId: string; equipmentId: string }
    >({
      query: ({ projectId, equipmentId }) => ({
        url: `/projects/${projectId}/equipment/${equipmentId}`,
      }),
      providesTags: (_result, _error, { equipmentId }) => [
        { type: 'Equipment', id: equipmentId },
      ],
    }),

    // ==========================================================================
    // CREATE
    // ==========================================================================

    createEquipment: builder.mutation<
      ApiResponse<Equipment>,
      { projectId: string; data: CreateEquipmentRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/equipment`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Equipment', 'EquipmentStats'],
    }),

    // ==========================================================================
    // UPDATE
    // ==========================================================================

    updateEquipment: builder.mutation<
      ApiResponse<Equipment>,
      { projectId: string; equipmentId: string; data: UpdateEquipmentRequest }
    >({
      query: ({ projectId, equipmentId, data }) => ({
        url: `/projects/${projectId}/equipment/${equipmentId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { equipmentId }) => [
        'Equipment',
        'EquipmentStats',
        { type: 'Equipment', id: equipmentId },
      ],
    }),

    // ==========================================================================
    // DELETE
    // ==========================================================================

    deleteEquipment: builder.mutation<
      ApiResponse<void>,
      { projectId: string; equipmentId: string }
    >({
      query: ({ projectId, equipmentId }) => ({
        url: `/projects/${projectId}/equipment/${equipmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Equipment', 'EquipmentStats'],
    }),

    // ==========================================================================
    // ASSIGN
    // ==========================================================================

    assignEquipment: builder.mutation<
      ApiResponse<Equipment>,
      { projectId: string; equipmentId: string; data: AssignEquipmentRequest }
    >({
      query: ({ projectId, equipmentId, data }) => ({
        url: `/projects/${projectId}/equipment/${equipmentId}/assign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { equipmentId }) => [
        'Equipment',
        'EquipmentStats',
        { type: 'Equipment', id: equipmentId },
      ],
    }),

    // ==========================================================================
    // RETURN
    // ==========================================================================

    returnEquipment: builder.mutation<
      ApiResponse<Equipment>,
      { projectId: string; equipmentId: string; data: ReturnEquipmentRequest }
    >({
      query: ({ projectId, equipmentId, data }) => ({
        url: `/projects/${projectId}/equipment/${equipmentId}/return`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { equipmentId }) => [
        'Equipment',
        'EquipmentStats',
        { type: 'Equipment', id: equipmentId },
      ],
    }),

    // ==========================================================================
    // LOG MAINTENANCE
    // ==========================================================================

    logEquipmentMaintenance: builder.mutation<
      ApiResponse<Equipment>,
      { projectId: string; equipmentId: string; data: MaintenanceLogRequest }
    >({
      query: ({ projectId, equipmentId, data }) => ({
        url: `/projects/${projectId}/equipment/${equipmentId}/maintenance`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { equipmentId }) => [
        'Equipment',
        'EquipmentStats',
        { type: 'Equipment', id: equipmentId },
      ],
    }),
  }),
});

export const {
  useGetEquipmentStatsQuery,
  useGetEquipmentQuery,
  useGetEquipmentByIdQuery,
  useCreateEquipmentMutation,
  useUpdateEquipmentMutation,
  useDeleteEquipmentMutation,
  useAssignEquipmentMutation,
  useReturnEquipmentMutation,
  useLogEquipmentMaintenanceMutation,
} = equipmentApi;
